import { getListFromApiResponse } from "../Pages/utils/apiListMappers";

/** Prefix for timesheet-only member strings not resolved from the users API list. */
export const LEGACY_USER_FILTER_PREFIX = "legacy:";

/** Prefer first + last; never use email as the visible label. */
export function getUserDisplayName(user) {
  if (!user || typeof user !== "object") return "";
  const fn = String(user.firstName ?? "").trim();
  const ln = String(user.lastName ?? "").trim();
  if (fn || ln) return [fn, ln].filter(Boolean).join(" ");

  const rawName = String(user.name ?? "").trim();
  if (rawName && !rawName.includes("@")) return rawName;

  const un = String(user.userName ?? "").trim();
  if (un && !un.includes("@")) return un;

  return "";
}

/** Two-letter initials for {@link ../Utility/Avatar/Avatar.jsx} when no image URL. */
export function getUserAvatarInitials(user) {
  const fn = String(user?.firstName ?? "").trim();
  const ln = String(user?.lastName ?? "").trim();
  if (fn && ln) return `${fn[0]}${ln[0]}`.toUpperCase();

  const display = getUserDisplayName(user);
  if (!display) return "?";
  const parts = display.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return display.length >= 2 ? display.slice(0, 2).toUpperCase() : display[0].toUpperCase();
}

export function initialsFromDisplayName(name) {
  const s = String(name ?? "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return s.length >= 2 ? s.slice(0, 2).toUpperCase() : s[0].toUpperCase();
}

/** Optional profile image from common API shapes. */
export function getUserAvatarImageUrl(user) {
  if (!user || typeof user !== "object") return undefined;
  return (
    user.profileImageUrl ??
    user.avatarUrl ??
    user.profilePicture ??
    user.imageUrl ??
    user.photoUrl ??
    undefined
  );
}

/**
 * @returns {{ value: string, label: string, showAvatar: boolean, avatar: string, avatarUrl?: string } | null}
 */
export function userToDropdownOption(user) {
  const id = user?.id ?? user?.userId;
  if (id == null || id === "") return null;
  const label = getUserDisplayName(user);
  if (!label) return null;
  const avatarUrl = getUserAvatarImageUrl(user);
  return {
    value: String(id),
    label,
    showAvatar: true,
    avatar: getUserAvatarInitials(user),
    ...(avatarUrl ? { avatarUrl } : {}),
  };
}

export function buildUserDropdownOptionsFromUsersData(usersData) {
  const list = getListFromApiResponse(usersData);
  const opts = list.map(userToDropdownOption).filter(Boolean);
  return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

export function legacyMemberOption(memberName) {
  const label = String(memberName ?? "").trim();
  if (!label) return null;
  return {
    value: `${LEGACY_USER_FILTER_PREFIX}${encodeURIComponent(label)}`,
    label,
    showAvatar: true,
    avatar: initialsFromDisplayName(label),
  };
}

/**
 * Client-side row filter: selected values are user ids (from API) or legacy-prefixed member strings.
 */
export function entryMatchesUserFilter(entry, selectedIds, usersData) {
  if (!selectedIds?.length) return true;
  const list = getListFromApiResponse(usersData);
  const memberName = entry.member || entry.assignee || entry.owner || "";
  const entryUid = entry.userId ?? entry.memberId ?? entry.ownerId;

  for (const sel of selectedIds) {
    const s = String(sel);
    if (s.startsWith(LEGACY_USER_FILTER_PREFIX)) {
      try {
        const raw = decodeURIComponent(s.slice(LEGACY_USER_FILTER_PREFIX.length));
        if (memberName === raw) return true;
      } catch {
        /* ignore */
      }
      continue;
    }
    const u = list.find((x) => String(x.id ?? x.userId) === s);
    if (u) {
      const d = getUserDisplayName(u);
      if (d && memberName === d) return true;
      if (entryUid != null && String(entryUid) === s) return true;
    } else if (memberName === s) {
      return true;
    }
  }
  return false;
}

/** First selected user id for API query params (skips legacy-only selections). */
export function getFirstResolvedUserId(appliedUserSelection) {
  const first = appliedUserSelection?.[0];
  if (first == null || first === "") return undefined;
  const s = String(first);
  if (s.startsWith(LEGACY_USER_FILTER_PREFIX)) return undefined;
  const n = Number(s);
  if (Number.isFinite(n) && String(n) === s) return n;
  return s;
}

/**
 * All selected user ids for API query params (skips `legacy:` entries).
 * Use with comma-separated or repeated `userId` query params.
 */
export function getResolvedUserIdsForApi(appliedUserSelection) {
  if (!Array.isArray(appliedUserSelection) || appliedUserSelection.length === 0) return [];
  const out = [];
  for (const sel of appliedUserSelection) {
    if (sel == null || sel === "") continue;
    const s = String(sel);
    if (s.startsWith(LEGACY_USER_FILTER_PREFIX)) continue;
    const n = Number(s);
    if (Number.isFinite(n) && String(n) === s) out.push(String(n));
    else out.push(s);
  }
  return out;
}
